/**
 * Private vs Shared for organisation courses (06_team_courses.sql, 30 Sep 2026).
 *
 * Every organisation course is PRIVATE unless it has a row in
 * org_course_visibility saying 'org' — so new courses start private with no
 * extra write. Shared courses are seen and edited by the organisation's
 * Owner, Admins and Creators; a PRIVATE course by its creator alone — not even
 * the Owner (08_private_is_private.sql, 1 Oct 2026). The creator switches it
 * either way; the Owner and Admins may only make a shared course private
 * again. The database enforces who.
 *
 * Courses outside organisations (all of production) have no visibility: they
 * are simply their creator's.
 */

import { supabase } from './supabase'

export type Visibility = 'private' | 'org'

/** Visibility of these courses; anything missing is private. */
export async function getVisibility(courseIds: string[]): Promise<Map<string, Visibility>> {
  const map = new Map<string, Visibility>()
  if (!courseIds.length) return map
  const { data, error } = await supabase.from('org_course_visibility').select('course_id, visibility').in('course_id', courseIds)
  if (error) { console.warn('[visibility] read failed:', error.message); return map }
  for (const r of (data ?? []) as Array<{ course_id: string; visibility: Visibility }>) map.set(r.course_id, r.visibility)
  return map
}

/** Share with the organisation, or make private again. Throws when not allowed. */
export async function setVisibility(courseId: string, visibility: Visibility, userId: string): Promise<void> {
  const { error } = await supabase.from('org_course_visibility')
    .upsert({ course_id: courseId, visibility, updated_by: userId }, { onConflict: 'course_id' })
  if (error) throw new Error(error.message.includes('row-level security')
    ? 'Only the course’s creator, the Owner or an Admin can change who sees it.'
    : error.message)
}


/**
 * Bring your own courses from before organisations into this organisation (7 Oct 2026) —
 * what "Share with organisation" does first for such a course. Only the creator's own,
 * only ones still outside any organisation; the database also requires the creator to be
 * Owner, Admin or Creator there (courses_org_guard). They arrive private; sharing is the
 * next step. Links, codes, narration and images do not change — only which organisation
 * the course belongs to.
 */
export async function moveIntoOrg(courseIds: string[], orgId: string, userId: string): Promise<void> {
  if (!courseIds.length) return
  const { data, error } = await supabase.from('courses').update({ org_id: orgId })
    .in('id', courseIds).eq('created_by', userId).is('org_id', null).select('id')
  if (error) throw new Error(error.message.includes('row-level security')
    ? 'Only Creators can bring courses into the organisation.'
    : error.message)
  if ((data ?? []).length !== courseIds.length) throw new Error('Some of these courses could not be moved. Refresh and try again.')
}

/**
 * Should this course appear in a person's lists (Home's recent courses and
 * search)? The same rule as the Courses page's two tabs together: their own
 * courses, the organisation's SHARED courses, and courses from before
 * organisations. Another member's PRIVATE course never appears — not even to
 * the Owner or Admins, who may open it (RLS lets them) but do not get it in
 * their lists (decided 30 Sep 2026).
 */
export function isListed(c: { id: string; org_id?: string | null; created_by: string }, userId: string | null | undefined, vis: Map<string, Visibility>): boolean {
  if (!c.org_id?.startsWith('org_')) return true
  return c.created_by === userId || vis.get(c.id) === 'org'
}

/** `isListed` over a whole list, reading visibility for the organisation courses in it. */
export async function listedCourses<C extends { id: string; org_id?: string | null; created_by: string }>(courses: C[], userId: string | null | undefined): Promise<C[]> {
  const others = courses.filter(c => c.org_id?.startsWith('org_') && c.created_by !== userId)
  if (!others.length) return courses
  const vis = await getVisibility(others.map(c => c.id))
  return courses.filter(c => isListed(c, userId, vis))
}
